import { Fragment, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import SkeletonRow from "components/SkeletonRow";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAppSelector } from "redux/hooks";
import { selectIsSupport } from "redux/slices/session";
import ReprocessIntakeDialog from "./ReprocessIntakeDialog";
import { DocumentIntake } from "generated/graphql";
import IntakeRow, { ActionItem } from "./IntakeRow";
import DeleteIntakeDialog from "./DeleteIntakeDialog";
import RenameIntakeDialog from "./RenameIntakeDialog";
import DocumentSegmentRow from "./DocumentSegmentRow";
import {
  Create,
  Delete,
  Edit,
  GetApp,
  Info,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import BulkDeleteIntakeDialog from "./BulkDeleteIntakeDialog";
import { backend, COVERSHEET_PROPS } from "Backend";
import {
  selectDocumentProperties,
  selectVisibleDocumentProperties,
  selectVisibleDocumentPropertiesThatHaveValues,
} from "redux/slices/clientLens";
import EditDocumentPropertiesDialog from "./EditDocumentPropertiesDialog";
import { P } from "theme/typography";
import theme from "theme";
import UploadDialog from "shared/multipart-upload/UploadDialog";
import { useNavigate } from "react-router";
import UploaderClassImpl from "shared/UploaderClassImpl";
import { Uploader } from "shared/multipart-upload/Uploader";
import { fetchCaseIntakes, selectCaseClaimantName, selectIntakes } from "redux/slices/case";
import doBulkDownload from "helpers/doBulkDownload";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { TableColumns } from "routes/lenses/lens/case/intakes/TableColumns";
import { selectCollectUsers, selectUsers } from "redux/slices/client";

interface IntakesTableProps {
  lensId: number;
  caseId: number;
}

const IntakesTable = ({ lensId, caseId }: IntakesTableProps) => {
  const snackpatch = useAppDispatchWithSnackbar();
  const intakes: DocumentIntake[] = useAppSelector(selectIntakes);
  const [selectedIntakes, setSelectedIntakes] = useState<DocumentIntake[]>([]);
  const [loading, setLoading] = useState(false);
  const iAmSupport = useAppSelector(selectIsSupport);
  const [showProperties, setShowProperties] = useState(false);
  const visibleDocumentProperties = useAppSelector(selectVisibleDocumentPropertiesThatHaveValues);
  const nonHiddenDocumentProperties = useAppSelector(selectVisibleDocumentProperties);
  const documentProperties = useAppSelector(selectDocumentProperties);
  const showCoversheets = !!documentProperties.find((dp) => dp.key === COVERSHEET_PROPS.DATE);
  const [, setUploads] = useState<Uploader[]>([]);
  const claimantName = useAppSelector(selectCaseClaimantName);
  const navigate = useNavigate();
  const columns = (): TableColumns => {
    const columns: TableColumns = {
      select: true,
      actions: true,
      name: true,
      uploadedBy: true,
      uploadDate: true,
      fileSize: true,
      pages: true,
      readQuality: true,
      status: true,
    };

    if (iAmSupport) {
      columns["id"] = true;
      columns["processedOn"] = true;
    }

    if (showCoversheets) {
      columns["coversheets"] = true;
    }

    return columns;
  };
  const [textQuery, setTextQuery] = useState<string | undefined>(undefined);
  const refreshIntakes = (showLoading: boolean) => {
    if (showLoading) setLoading(true);
    snackpatch(fetchCaseIntakes({ caseId, forceRefresh: true }), {
      errorMessage: "Refresh documents failed. Please try again later.",
    }).then(() => {
      if (showLoading) setLoading(false);
    });
  };

  const filteredIntakes: DocumentIntake[] =
    textQuery !== undefined
      ? intakes.filter((i) => i.name.toLowerCase().indexOf(textQuery.toLowerCase()) !== -1)
      : intakes;

  const [expandedIntakeId, setExpandedIntakeId] = useState<number | undefined>(undefined);
  const [selectedIntakeId, setSelectedIntakeId] = useState<number | undefined>(undefined);
  const [deleteIntakeOpen, setDeleteIntakeOpen] = useState(false);
  const [renameIntakeOpen, setRenameIntakeOpen] = useState(false);
  const [bulkDeleteIntakeOpen, setBulkDeleteIntakeOpen] = useState(false);
  const closeDeleteIntake = () => {
    setSelectedIntakeId(undefined);
    setDeleteIntakeOpen(false);
    refreshIntakes(false);
  };
  const [addIntakesOpen, setAddIntakesOpen] = useState(false);
  const users = useAppSelector(selectUsers);
  const collectUsers = useAppSelector(selectCollectUsers);

  /** Reprocessing */
  const [reprocessIntakeOpen, setReprocessIntakeOpen] = useState(false);
  const closeReprocessIntake = () => {
    setSelectedIntakeId(undefined);
    setReprocessIntakeOpen(false);
  };
  /** End Reprocessing */

  /** Edit Custom Properties */
  const [editDocumentProperties, setEditDocumentProperties] = useState(false);
  const closeEditDocumentProperties = () => {
    setSelectedIntakeId(undefined);
    setEditDocumentProperties(false);
  };
  /** End Edit Custom Properties */

  /**
   * Intake Actions
   */
  const actionItems = (intake: DocumentIntake) => {
    const ret: ActionItem[] = [];
    const open = expandedIntakeId !== undefined && expandedIntakeId === intake.id;

    if (iAmSupport) {
      ret.push(
        {
          name: "Reprocess",
          icon: <RefreshIcon color="action" />,
          action: () => {
            setSelectedIntakeId(intake.id);
            setReprocessIntakeOpen(true);
          },
        },
        {
          name: open ? "Close" : "Expand",
          icon: open ? <KeyboardArrowUp /> : <KeyboardArrowDown />,
          action: () => {
            setExpandedIntakeId((currExpandedId) => (currExpandedId !== undefined ? undefined : intake.id));
          },
        }
      );
    }
    ret.push(
      {
        name: "Edit Properties",
        icon: <Edit color="action" />,
        action: () => {
          setSelectedIntakeId(intake.id);
          setEditDocumentProperties(true);
        },
      },
      {
        name: "Rename",
        icon: <Create color="action" />,
        action: () => {
          setSelectedIntakeId(intake.id);
          setRenameIntakeOpen(true);
        },
      }
    );

    return ret;
  };

  const selectedIntake = useMemo(() => {
    return selectedIntakeId !== undefined ? intakes.find((i) => i.id === selectedIntakeId) : undefined;
  }, [selectedIntakeId, intakes]);

  const documentSegmentRows = (intake: DocumentIntake) => {
    return intake.documentSegments.map((documentSegment) => (
      <DocumentSegmentRow key={documentSegment.id} segment={documentSegment} intake={intake} caseId={caseId} />
    ));
  };

  const tableRows = (expandedIntakeId) => {
    return filteredIntakes.map((intake) => {
      return (
        <Fragment key={intake.id}>
          <IntakeRow
            intake={intake}
            caseId={caseId}
            lensId={lensId}
            actionItems={actionItems(intake)}
            columns={columns()}
            onSelect={(intake) => {
              if (selectedIntakes.find((i) => i.id === intake.id)) {
                setSelectedIntakes(selectedIntakes.filter((i) => i.id !== intake.id));
              } else {
                setSelectedIntakes([...selectedIntakes, intake]);
              }
            }}
            selected={selectedIntakes.find((i) => i.id === intake.id) ? true : false}
            documentProperties={visibleDocumentProperties}
            showDocumentProperties={showProperties}
            users={users}
            collectUsers={collectUsers}
          />
          {expandedIntakeId === intake.id && documentSegmentRows(intake)}
        </Fragment>
      );
    });
  };

  const tableHead = () => {
    return (
      <TableHead>
        <TableRow
          sx={{
            whiteSpace: "nowrap",
          }}>
          <TableCell sx={{ px: 0 }}>
            <Checkbox
              size="small"
              color="secondary"
              indeterminate={!!selectedIntakes.length && selectedIntakes.length !== filteredIntakes.length}
              checked={selectedIntakes.length ? true : false}
              onClick={() => {
                if (selectedIntakes.length) {
                  setSelectedIntakes([]);
                } else {
                  setSelectedIntakes(filteredIntakes);
                }
              }}
            />
          </TableCell>
          <TableCell>Action</TableCell>
          <TableCell>Name</TableCell>
          {iAmSupport && <TableCell>Document Id</TableCell>}
          <TableCell>Upload Date</TableCell>
          {iAmSupport && <TableCell>Last Process Date</TableCell>}
          <TableCell>File Size</TableCell>
          <TableCell>Pages</TableCell>
          <TableCell>Read Quality</TableCell>
          <TableCell style={{ width: "200px" }}>Status</TableCell>
          {showCoversheets && (
            <>
              <TableCell>Coversheet Date</TableCell>
              <TableCell>Approved Date</TableCell>
              <TableCell>Approved By</TableCell>
            </>
          )}
          {showProperties &&
            visibleDocumentProperties.map((property) => (
              <TableCell
                key={property.key}
                sx={{
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    whiteSpace: "nowrap",
                  }}>
                  {property.displayName}
                  {property.preventsLensHits && (
                    <Tooltip arrow title="Prevents lens hits">
                      <Info sx={{ ml: 1 }} fontSize="small" color="secondary" />
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
            ))}
          <TableCell>Uploaded By</TableCell>
        </TableRow>
      </TableHead>
    );
  };

  return (
    <Card sx={{ minHeight: 0, width: "100%", height: "100%" }}>
      <CardContent sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box
          display="flex"
          sx={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              border: selectedIntakes.length ? `1px solid ${theme.palette.grey[100]}` : "",
              borderRadius: "8px",
              px: 2,
              flex: 1,
            }}>
            <P>Bulk Actions</P>
            <Tooltip title="Delete" arrow>
              <span>
                <IconButton
                  disabled={selectedIntakes.length === 0}
                  id="document_table_delete"
                  onClick={() => {
                    if (selectedIntakes.length) {
                      setBulkDeleteIntakeOpen(true);
                    }
                  }}>
                  <Delete />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Download" arrow>
              <span>
                <IconButton
                  disabled={selectedIntakes.length === 0}
                  id="document_table_download"
                  onClick={() =>
                    doBulkDownload(selectedIntakes.map((intake) => backend.getIntakeDownloadUrl(intake.id)))
                  }>
                  <GetApp />
                </IconButton>
              </span>
            </Tooltip>
            {selectedIntakes.length > 0 && (
              <P variant="xs">
                {selectedIntakes.length} {selectedIntakes.length === 1 ? "Document" : "Documents"} Selected
              </P>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1,
              flex: 1,
            }}>
            <Box>
              {visibleDocumentProperties.length > 0 && (
                <Button
                  sx={{
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => {
                    setShowProperties(!showProperties);
                  }}
                  endIcon={showProperties ? <Visibility /> : <VisibilityOff />}>
                  {showProperties ? `Hide ` : `Show ${visibleDocumentProperties.length}`} Properties
                </Button>
              )}
            </Box>
            <Tooltip title="Refresh" arrow>
              <IconButton disabled={loading} onClick={() => refreshIntakes(true)} size="large">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add" arrow>
              <IconButton id="document_table_add" size="large" onClick={() => setAddIntakesOpen(true)}>
                <AddIcon />
              </IconButton>
            </Tooltip>
            <TextField
              placeholder="Find Documents"
              value={textQuery}
              onChange={(evt) => setTextQuery(evt.target.value)}
              fullWidth
            />
          </Box>
        </Box>

        <TableContainer>
          <Table size="small" stickyHeader>
            {tableHead()}
            <TableBody>
              {loading ? (
                Array(5)
                  .fill(0)
                  .map((_x, idx) => <SkeletonRow key={idx} columnNum={Object.keys(columns()).length} />)
              ) : (
                <>{tableRows(expandedIntakeId)}</>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <BulkDeleteIntakeDialog
          open={bulkDeleteIntakeOpen}
          close={() => {
            setSelectedIntakes([]);
            setBulkDeleteIntakeOpen(false);
          }}
          intakes={selectedIntakes}
        />

        {selectedIntake && (
          <DeleteIntakeDialog open={deleteIntakeOpen} close={closeDeleteIntake} intake={selectedIntake} />
        )}
        {selectedIntake && (
          <ReprocessIntakeDialog open={reprocessIntakeOpen} close={closeReprocessIntake} intakeId={selectedIntake.id} />
        )}
        {selectedIntake && (
          <RenameIntakeDialog
            open={renameIntakeOpen}
            close={() => {
              setRenameIntakeOpen(false);
              setSelectedIntakeId(undefined);
            }}
            intake={selectedIntake}
          />
        )}
        {selectedIntake && (
          <EditDocumentPropertiesDialog
            open={editDocumentProperties}
            close={closeEditDocumentProperties}
            intake={selectedIntake}
            documentProperties={nonHiddenDocumentProperties}
          />
        )}

        {addIntakesOpen && (
          <UploadDialog
            open={addIntakesOpen}
            close={() => {
              setAddIntakesOpen(false);
              void snackpatch(fetchCaseIntakes({ caseId, forceRefresh: true }), {
                errorMessage: "Failed to fetch case documents",
              });
            }}
            caseId={caseId}
            claimantName={claimantName}
            onUnAuthorized={() => navigate("/logout")}
            setUploads={setUploads}
            UploaderClazz={UploaderClassImpl}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default IntakesTable;
